<!--
  Content Listing Block
  This component is used to display posts and filter them.

  Depending on the post type, the filters will be displayed differently.
  - Insight (Insights Hub): Insight Filters (Manually Authored Filters)
  - Press Release (Press Center): Press Release Filters (Content Topics)
  - No Post Type (Search Page): No Post Type Filters (Content Types)

  Props:
  - cms: JSON object containing the content listing block component data
    - filters: JSON object containing the filters data
    - post_type: object containing the post type
      - slug: string containing the post type slug
      - name: string containing the post type name
    - add_promo_cta: boolean indicating if the promo cta should be added
    - promo_cta: JSON object containing the promo cta data

-->

<template>
  <div class="canvas-container" ref="contentListingBlockContainer">
    <div class="canvas-grid content-listing__grid">
      <!-- Post Filters -->
      <content-listing-block-filters v-if="!is_archive" :filters="filters" :variation="variation" :post_type="post_type"
        :content_type_counts="content_type_counts" :total_items="total_items"
        :search="q_search" :order="q_order" :orderby="q_orderby" @update-terms="updateTerms" @reset-terms="resetTerms" @update-search="updateSearch"
        @update-sort="updateSort" @update-post-type="updatePostType" @reset-post-type="resetPostType"
        @submit-filters="submitFilters" :api_call_status="api_call_status"></content-listing-block-filters>

      <!-- Post Grid -->
      <content-listing-block-grid v-if="posts.list.length > 0 && api_call_status === 0" :posts="posts.list"
        :add_promo_cta="add_promo_cta" :promo_cta="promo_cta" :fallback_image_dark="fallback_image_dark" :fallback_image_light="fallback_image_light" :ppp="ppp"
        class_name="content-listing__list g-col-4 g-start-1 g-col-700-8 g-start-700-1 g-col-1000-10 g-start-1000-3" :variation="variation"></content-listing-block-grid>

      <!-- Loader -->
      <template v-if="api_call_status === 1">
        <div class="content-listing__loader g-col-4 g-start-1 g-col-700-8 g-start-700-1 g-col-1000-10 g-start-1000-3">
          <div class="content-listing__loader-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 50 50" xml:space="preserve">
              <path fill="#0D2834"
                d="M32.696 8.132c-9.444-4.155-20.47.132-24.625 9.577l3.723 1.638c3.25-7.388 11.875-10.742 19.264-7.491z">
                <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25"
                  to="360 25 25" dur="0.6s" repeatCount="indefinite" />
              </path>
            </svg>
          </div>
        </div>
      </template>

      <!-- No results -->
      <template v-if="posts.list.length === 0 && api_call_status === 0">
        <div
          class="content-listing__no-results g-col-4 g-start-1 g-col-700-8 g-start-700-1 g-col-1000-8 g-start-1000-3">
          <div class="content-listing__no-results-text">
            <h5 class="content-listing__no-results-text-headline">We&rsquo;re sorry, your search {{ q_search ? `for
              &quot;${q_search}&quot;` : '' }} didn&rsquo;t return any results.</h5>
            <p class="content-listing__no-results-text-description"
              v-if="variation !== 'insights-hub' && variation !== 'press-center'">Try searching again using different
              {{ q_search ? 'words or check the spelling' : 'filters' }}. </p>
            <p class="content-listing__no-results-text-description" v-else>See our latest {{ variation === 'insights-hub' ? 'insights' : 'press releases' }} below, or search
              again.</p>
          </div>
          <div class="content-listing__no-results-links"
            v-if="variation !== 'insights-hub' && variation !== 'press-center'">
            <h6 v-if="error.headline" class="content-listing__no-results-links-headline">{{ error.headline }}</h6>
            <ul v-if="error.links.length" class="content-listing__no-results-links-list bare-list">
              <li v-for="link in error.links" :key="link.id" class="content-listing__no-results-links-item">
                <a :href="link.link.url" :target="link.link.target"
                  class="content-listing__no-results-links-link canvas-btn canvas-btn--tertiary canvas-btn--tertiary-lg"
                  data-skin="dark">
                  <span class="canvas-btn__text-wrapper" :data-text="link.link.title">
                    <span class="canvas-btn__text">
                      {{ link.link.title }}
                    </span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- Featured Insights -->
      <content-listing-block-grid
        v-if="posts.list.length === 0 && api_call_status === 0 && variation === 'insights-hub'"
        :posts="latest_insights" :add_promo_cta="add_promo_cta" :promo_cta="promo_cta" :fallback_image_dark="fallback_image_dark" :fallback_image_light="fallback_image_light" :ppp="ppp" :variation="variation"
        class_name="content-listing__list content-listing__list--featured g-col-4 g-start-1 g-col-700-8 g-start-700-1 g-col-1000-10 g-start-1000-3"></content-listing-block-grid>

      <!-- Featured Press Releases -->
      <content-listing-block-grid
        v-if="posts.list.length === 0 && api_call_status === 0 && variation === 'press-center'"
        :posts="latest_press_releases" :add_promo_cta="add_promo_cta" :promo_cta="promo_cta" :fallback_image_dark="fallback_image_dark" :fallback_image_light="fallback_image_light" :ppp="ppp" :variation="variation"
        class_name="content-listing__list content-listing__list--featured g-col-4 g-start-1 g-col-700-8 g-start-700-1 g-col-1000-10 g-start-1000-3"></content-listing-block-grid>


      <!-- Pagination -->
      <footer class="content-listing__footer g-col-4 g-start-1 g-col-700-8 g-col-1000-10 g-start-1000-3"
        v-if="api_call_status === 0 && posts.list.length > 0">
        <div v-if="total_pages > 1" class="content-listing__pagination-container">
          <vue-awesome-paginate :total-items="total_items" :items-per-page="ppp" v-model="page" :max-pages-shown="3"
            :show-breakpoint-buttons="true" :show-breakpoint-buttons-always="true" :disable-prev-next-when-ends="true"
            @click="goToPage(page)">

            <template #prev-button>
              <span :class="{ 'paginate-button-disabled': page === 1 }">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                  <path fill="#fff" fill-opacity=".01" d="M0 .513h24v24H0z" />
                  <path
                    d="m11.896 2.013-10.174 9.98a.735.735 0 0 0 0 1.049l10.164 9.97L13.5 21.43l-9.094-8.92L13.5 3.586zM8.25 11.388v2.25H22.5a2.24 2.24 0 0 1-.306-1.125c0-.41.114-.794.306-1.125z"
                    fill="#003661" />
                </svg>
              </span>
            </template>

            <template #next-button>
              <span :class="{ 'paginate-button-disabled': page === total_pages }">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                  <path fill="#fff" fill-opacity=".01" d="M0 .513h24v24H0z" />
                  <path
                    d="m12.105 2.013 10.173 9.98a.735.735 0 0 1 0 1.049l-10.164 9.97L10.5 21.43l9.094-8.92L10.5 3.586zm3.645 9.375v2.25H1.5c.192-.332.306-.715.306-1.125s-.114-.794-.306-1.125z"
                    fill="#003661" />
                </svg>
              </span>
            </template>
          </vue-awesome-paginate>
        </div>
        <div v-if="total_items > 0" class="content-listing__pagination-total">
          Showing {{ total_items > 1 ? `${current_range.start} - ${current_range.end} of` : '' }} {{ total_items }}
          result{{
            total_items > 1 ? 's' : '' }}
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import ContentListingBlockFilters from "./ContentListingBlockFilters.vue";
import ContentListingBlockGrid from "./ContentListingBlockGrid.vue";

export default {
  name: "ContentListingBlock",
  components: {
    ContentListingBlockFilters,
    ContentListingBlockGrid,
  },
  props: ["cms"],
  methods: {
    updatePostType(post_type) {
      // Extract template from post type if it includes template info
      if (typeof post_type === 'string' && post_type.includes('::')) {
        const parts = post_type.split('::');
        this.post_type = parts[0]; // 'page'
        this.q_template = parts[1]; // 'product'
      } else {
        this.post_type = post_type;
        this.q_template = ''; // Reset template if not applicable
      }
      this.page = 1;
      this.getPosts();
    },
    resetPostType() {
      this.post_type = "insight,press-release,product,page,posts";
      this.q_template = ''; // Reset template filter
      this.page = 1;
      this.getPosts();
    },
    getData(url) {
      return new Promise((resolve, reject) => {
        fetch(url)
          .then((response) => {
            return response.json();
          })
          .then((data) => resolve(data))
          .catch((error) => reject(error));
      });
    },
    initialCall() {
      const parse_cms = JSON.parse(this.cms);
      // console.log(parse_cms);
      // set variation
      this.variation = parse_cms.variation || null;
      // set filters
      this.filters.list = parse_cms.filters || [];
      // set post type
      this.post_type = parse_cms.post_type || {};
      // set add promo cta
      this.add_promo_cta = parse_cms.add_promo_cta || false;
      // set promo cta
      this.promo_cta = parse_cms.promo_cta || {};
      // set fallback images
      this.fallback_image_dark = parse_cms.fallback_image_dark || {};
      this.fallback_image_light = parse_cms.fallback_image_light || {};
      // set error
      this.error = parse_cms.error || {};
      // set is_archive
      this.is_archive = parse_cms.is_archive || false;
      this.latest_insights = parse_cms.latest_insights || [];
      this.latest_press_releases = parse_cms.latest_press_releases || [];
      this.excluded_post_ids = parse_cms.excluded_post_ids || [];
      // Set default sort values based on variation
      if (this.variation === 'search-page') {
        this.q_order = "relevance";
        this.q_orderby = "relevance";
      }
      // check URL params on initial load
      if (this.parsedURL.search.length > 0) {
        this.filters.list.forEach((item) => {
          const paramTerm = this.parsedURL.searchParams.get(item.taxonomy);
          if (paramTerm) {
            // Handle comma-separated values
            const termIds = paramTerm.split(',').map(id => id.trim());
            const selectedTermIds = [];
            item.terms.forEach((term) => {
              if (termIds.includes(term.term_id.toString())) {
                term.selected = true;
                selectedTermIds.push(term.term_id.toString());
                item.selected_term = term.name;
              }
            });
            // Build q_term with comma-separated values for this taxonomy
            if (selectedTermIds.length > 0) {
              this.q_term += `&${item.taxonomy}=${selectedTermIds.join(',')}`;
            }
          }
        });
      }


      // check if is_archive is true on initial load
      if (parse_cms.is_archive) {
        this.q_term = `&${parse_cms.archive.taxonomy}=${parse_cms.archive.term_id}`;
      }

      // call inital posts
      this.getPosts();
    },
    getPosts() {
      this.api_call_status = 1;
      // Create URL object for query parameters
      const apiUrl = new URL('/wp-json/api/get-posts/', window.location.origin);
      // Add query parameters
      if (this.q_term) {
        // Parse q_term which contains &taxonomy=value1,value2,value3 format
        const termParams = new URLSearchParams(this.q_term);
        termParams.forEach((value, key) => {
          apiUrl.searchParams.set(key, value);
        });
      }
      apiUrl.searchParams.set('ppp', this.ppp);
      apiUrl.searchParams.set('order', this.q_order);
      apiUrl.searchParams.set('orderby', this.q_orderby);
      apiUrl.searchParams.set('page', this.page);
      apiUrl.searchParams.set('post_type', this.post_type.slug || this.post_type);

      // add search param if search is not empty
      if (this.q_search.length > 0) {
        apiUrl.searchParams.set('search', this.q_search);
      }

      // add excluded post ids if excluded post ids is not empty
      if (this.excluded_post_ids.length > 0) {
        apiUrl.searchParams.set('excluded_ids', this.excluded_post_ids);
      }

      // add template filter if template is set
      if (this.q_template.length > 0) {
        apiUrl.searchParams.set('template', this.q_template);
      }

      // search page will not have post type, the rest-api will default to all post types
      // if (this.variation !== 'search-page') {
      //   apiUrl.searchParams.set('post_type', this.posttype);
      // }
      this.getData(apiUrl.toString()).then((data) => {
        this.total_pages = data.results.total_pages;
        this.total_items = data.results.total_items;
        this.current_range = data.results.current_range;
        this.content_type_counts.list = data.results.content_type_counts;
        // Debug pagination values
        // console.log('Pagination Debug:', {
        //   total_pages: this.total_pages,
        //   ppp: this.ppp,
        //   totalItems: this.total_items,
        //   currentPage: this.page
        // });
        this.posts.list = data.results.posts;
        this.api_call_status = 0;
        this.isInitialLoad = false;
      });
      // update URL
      let replaceParams = this.q_term.slice();
      replaceParams = replaceParams.slice(1);

      // update params only for insights-hub and press-center
      // update only the s param for search-page
      // don't update any params for archive pages
      if (this.variation == 'insights-hub' || this.variation == 'press-center') {
        this.parsedURL.search = `${replaceParams}`;
        history.replaceState({}, "", this.parsedURL);
      } else if (this.variation == 'search-page' || this.variation == 'archive') {
        this.parsedURL.searchParams.set("s", this.q_search);
        history.replaceState({}, "", this.parsedURL);
        this.updateSearchFor();
      }
    },
    updateTerms(cbx) {
      this.q_term = "";
      const targetTaxonomy = this.filters.list.find(
        (item) => item.taxonomy === cbx.taxonomy
      );
      if (targetTaxonomy) {
        targetTaxonomy.terms.forEach((term) => {
          term.selected =
            cbx.selected && term.term_id === cbx.term_id ? true : false;
        });
      }
      this.filters.list.forEach((item) => {
        const selected_terms = [];
        item.terms.forEach((term) => {
          if (term.selected) {
            selected_terms.push(term.term_id);
          }
        });
        if (selected_terms.length > 0) {
          this.q_term += `&${item.taxonomy}=${selected_terms.join(',')}`;
        }
      });
      this.getPosts();
    },
    resetTerms(v) {
      this.q_term = "";
      const targetTaxonomy = this.filters.list.find(
        (item) => item.taxonomy === v.taxonomy
      );
      // set all term to false
      if (targetTaxonomy) {
        targetTaxonomy.terms.forEach((term) => {
          term.selected = false;
        });
      }
      this.filters.list.forEach((item) => {
        const selected_terms = [];
        item.terms.forEach((term) => {
          if (term.selected) {
            selected_terms.push(term.term_id);
          }
        });
        if (selected_terms.length > 0) {
          this.q_term += `&${item.taxonomy}=${selected_terms.join(',')}`;
        }
      });
      this.getPosts();
    },
    updateSearch(s) {
      this.q_search = s;
      this.page = 1;
      this.getPosts();
    },
    updateSort(order, orderby) {
      this.q_order = order;
      this.q_orderby = orderby;
      this.page = 1;
      this.getPosts();
    },
    clearFilters() {
      this.q_search = "";
      this.q_term = "";
      this.q_orderby = this.variation === 'search-page' ? "relevance" : "date";
      this.q_order = this.variation === 'search-page' ? "relevance" : "DESC";
      this.filters.list.forEach((item) => {
        item.terms.forEach((term) => {
          term.selected = false;
        });
      });
      this.page = 1;
      this.getPosts();
    },
    goToPage(pageNum) {
      // scroll to top of the component
      this.$nextTick(() => {
        if (this.$refs.contentListingBlockContainer) {
          this.$refs.contentListingBlockContainer.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      });

      this.page = pageNum;
      this.getPosts();
    },
    submitFilters(filterData) {
      // console.log('Submit filters called with:', filterData);

      // Update search query
      this.q_search = filterData.search || "";

      // Reset terms and build new query string
      this.q_term = "";
      this.filters.list.forEach((item) => {
        // Reset all terms for this taxonomy
        item.terms.forEach((term) => {
          term.selected = false;
        });

        // Set selected terms based on filter data
        if (filterData.filters && filterData.filters[item.taxonomy]) {
          const selectedTermIds = filterData.filters[item.taxonomy].map(term => term.term_id);
          item.terms.forEach((term) => {
            if (selectedTermIds.includes(term.term_id)) {
              term.selected = true;
            }
          });

          // Add to query string
          if (selectedTermIds.length > 0) {
            this.q_term += `&${item.taxonomy}=${selectedTermIds.join(',')}`;
          }
        }
      });

      // Reset to first page and get posts
      this.page = 1;
      this.getPosts();
    },
    updateSearchFor() {
      const searchfor = document.querySelectorAll('.js-searchfor');
      if (searchfor.length > 0) {
        searchfor.forEach(item => {
          item.textContent = this.q_search;
        });
      }
    }
  },
  data() {
    const parsedURL = new URL(window.location.href);
    return {
      variation: null,
      filters: { list: [] },
      posts: { list: [] },
      content_type_counts: { list: [] },
      page: 1,
      total_pages: 0,
      total_items: 0,
      current_range: { start: 0, end: 0 },
      post_type: {},
      q_term: "",
      q_order: "DESC", // Will be updated in initialCall based on variation
      q_orderby: "date", // Will be updated in initialCall based on variation
      ppp: 10,
      add_promo_cta: false,
      promo_cta: {},
      fallback_image_dark: {},
      fallback_image_light: {},
      isInitialLoad: true,
      api_call_status: 1,
      q_search: parsedURL.searchParams.get("s") || "",
      q_template: "", // Template filter for pages
      parsedURL,
      error: {},
      is_archive: false,
      latest_insights: [],
      latest_press_releases: [],
    };
  },
  mounted() {
    this.initialCall();
  },
};
</script>
